import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { ILeaseCustomer } from '@/types/admin/events';

export const DesktopResidentDetailsPanel = ({
  resident,
  flags,
  isUpdating,
  onCheckIn,
}: {
  resident: ILeaseCustomer;
  flags: { checkedIn: boolean; hasRsvp: boolean };
  isUpdating: boolean;
  onCheckIn: (resident: ILeaseCustomer, checkedIn: boolean) => void;
}) => {
  return (
    <View style={styles.container}>
      <View style={styles.headerRow}>
        <Text style={styles.headerTitle}>Resident Details</Text>
      </View>
      <View style={styles.body}>
        <View style={styles.infoRow}>
          <View style={styles.avatar}>
            <Text style={styles.avatarText}>
              {`${resident.firstName.charAt(0)}${resident.lastName.charAt(0)}`.toUpperCase()}
            </Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.name}>{resident.firstName} {resident.lastName}</Text>
            {!!resident.email && <Text style={styles.email}>{resident.email}</Text>}
            {!!resident.unitNumberSpace && <Text style={styles.unit}>Unit {resident.unitNumberSpace}</Text>}
            {!!resident.statusType && (
              <View style={styles.statusPill}><Text style={styles.statusPillText}>{resident.statusType.charAt(0).toUpperCase() + resident.statusType.slice(1)} resident</Text></View>
            )}
          </View>
        </View>

        <View style={styles.checkinSection}>
          <Text style={styles.sectionTitle}>Check-in info</Text>
          <View style={{ marginTop: 8 }}>
            {flags.checkedIn ? (
              <View style={styles.checkedInBadge}>
                <View style={styles.checkedDot} />
                <Text style={styles.checkedText}>Checked in</Text>
              </View>
            ) : (
              <View style={styles.notCheckedInBadge}>
                <View style={styles.notCheckedDot} />
                <Text style={styles.notCheckedText}>Not checked in</Text>
              </View>
            )}
            {flags.hasRsvp && (
              <View style={styles.rsvpInfo}><Text style={styles.rsvpText}>✓ RSVP'd to this event</Text></View>
            )}
          </View>
          <View style={{ marginTop: 16 }}>
            {!flags.checkedIn ? (
              <TouchableOpacity style={[styles.checkInBtn, isUpdating && { opacity: 0.7 }]} disabled={isUpdating} onPress={() => onCheckIn(resident, true)}>
                <Text style={styles.checkInBtnText}>Check in</Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity style={[styles.checkOutBtn, isUpdating && { opacity: 0.7 }]} disabled={isUpdating} onPress={() => onCheckIn(resident, false)}>
                <Text style={styles.checkOutBtnText}>Uncheck</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    borderRadius: 8,
    overflow: 'hidden',
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  body: {
    padding: 16,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  avatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: ADMIN_COLORS.PRIMARY + '20',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  avatarText: {
    fontSize: 20,
    fontWeight: '700',
    color: ADMIN_COLORS.PRIMARY,
  },
  name: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  email: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginTop: 2,
  },
  unit: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginTop: 2,
  },
  statusPill: {
    alignSelf: 'flex-start',
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    marginTop: 6,
  },
  statusPillText: {
    fontSize: 12,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
  checkinSection: {
    marginTop: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  checkedInBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: ADMIN_COLORS.SUCCESS + '15',
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.SUCCESS + '30',
    alignSelf: 'flex-start',
  },
  checkedDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: ADMIN_COLORS.SUCCESS,
    marginRight: 8,
  },
  checkedText: {
    fontSize: 12,
    fontWeight: '600',
    color: ADMIN_COLORS.SUCCESS,
  },
  notCheckedInBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: ADMIN_COLORS.TEXT_TERTIARY + '15',
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.TEXT_TERTIARY + '30',
    alignSelf: 'flex-start',
  },
  notCheckedDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: ADMIN_COLORS.TEXT_TERTIARY,
    marginRight: 8,
  },
  notCheckedText: {
    fontSize: 12,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
  rsvpInfo: {
    backgroundColor: ADMIN_COLORS.PRIMARY + '15',
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 8,
    marginTop: 8,
    alignSelf: 'flex-start',
  },
  rsvpText: {
    fontSize: 12,
    fontWeight: '500',
    color: ADMIN_COLORS.PRIMARY,
  },
  checkInBtn: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  checkInBtnText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  checkOutBtn: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignSelf: 'flex-start',
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
  },
  checkOutBtnText: {
    color: ADMIN_COLORS.TEXT_PRIMARY,
    fontSize: 14,
    fontWeight: '600',
  },
});


